import React, { useMemo, useState } from 'react';
import { Empty } from 'antd';
import ImageFx from '../../../commonComponents/ImageFx';
import { ModelImage } from './HeroGarment';
import styles from './modal.module.css';

const VIEWS = [
    { key: 'files', label: 'Studio files' },
    { key: 'angles', label: 'Angle preset' },
    { key: 'model', label: 'Model reference' },
    { key: 'pii', label: 'PII images' },
];

export default function ImagesTab({ sku, files }) {
    const [view, setView] = useState('files');
    const [selected, setSelected] = useState(0);
    const filesSorted = [...(files || [])].sort((a, b) => (b?.usageCount || 0) - (a?.usageCount || 0));
    const angles = sku?.angles || [];
    const piiImages = useMemo(() => {
        const article = sku?.article;
        if (!article) return [];
        const items = [];
        if (article.articleImage?.thumbPath?.url) {
            items.push({
                key: 'articleImage',
                image: article.articleImage,
                header: 'Hero Garment',
            });
        }
        if (article.tagImage?.thumbPath?.url) {
            items.push({
                key: 'tagImage',
                image: article.tagImage,
                header: 'Tag image',
            });
        }
        (article.otherImages || []).forEach((image, index) => {
            if (!image?.thumbPath?.url) return;
            items.push({
                key: image?._id || `other-${index}`,
                image,
                header: 'Other image',
            });
        });
        return items;
    }, [sku?.article]);

    const items =
        view === 'files'
            ? filesSorted.map(file => ({
                key: file?._id || file?.createdAt,
                image: file?.image,
                header: 'Studio file',
                caption: file?.createdAt ? new Date(file.createdAt).toLocaleString() : '',
                usageCount: file?.usageCount,
                isStudioCCFile: file?.isStudioCCFile,
            }))
            : view === 'angles'
                ? angles.map(angle => ({
                    key: angle?.angle?._id || angle?.angle?.name,
                    image: angle?.angle?.file,
                    header: angle?.angle?.name,
                    caption: angle?.angle?.shootType,
                    showVerifiedIcon: !!angle?.file,
                    shootStatus: sku?.shootStatus,
                }))
                : view === 'pii'
                    ? piiImages
                    : [];

    const current = items[selected] || items[0];
    const previewTitle =
        view === 'angles'
            ? 'Selected reference'
            : view === 'model'
                ? 'Model reference'
                : view === 'pii'
                    ? 'Selected PII image'
                    : 'Selected studio file';

    return (
        <div>
            <div className={styles.card}>
                <h3 className={styles.cardTitle}>Assigned angle preset</h3>
                <p style={{ margin: 0 }}>{sku?.anglePresetAlias || 'No angle preset'}</p>
            </div>
            <div className={styles.filters}>
                {VIEWS.map(item => (
                    <button
                        type="button"
                        key={item.key}
                        className={view === item.key ? `${styles.chip} ${styles.chipActive}` : styles.chip}
                        onClick={() => {
                            setView(item.key);
                            setSelected(0);
                        }}
                    >
                        {item.label}
                    </button>
                ))}
            </div>
            <div className={styles.imageLayout}>
                <div className={styles.card}>
                    {view === 'model' ? (
                        sku?.model?.image ? (
                            <ModelImage sku={sku} width="280px" height="360px" />
                        ) : (
                            <Empty description="No model reference" />
                        )
                    ) : items.length ? (
                        <div className={styles.imageGrid}>
                            {items.map((item, index) => (
                                <div
                                    key={item.key || index}
                                    onClick={() => setSelected(index)}
                                    style={{ cursor: 'pointer' }}
                                    className={index === selected ? styles.selectedThumb : undefined}
                                >
                                    <ImageFx
                                        image={item.image}
                                        allowFullSize
                                        header={item.header}
                                        caption={item.caption}
                                        usageCount={item.usageCount}
                                        isStudioCCFile={item.isStudioCCFile}
                                        showVerifiedIcon={item.showVerifiedIcon}
                                        shootStatus={item.shootStatus}
                                        width="140px"
                                        height="180px"
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Empty />
                    )}
                </div>
                <div className={styles.card}>
                    <h3 className={styles.cardTitle}>{previewTitle}</h3>
                    {view === 'model' ? (
                        <ModelImage sku={sku} width="100%" height="360px" />
                    ) : current ? (
                        <ImageFx
                            image={current.image}
                            allowFullSize
                            header={current.header}
                            caption={current.caption}
                            usageCount={current.usageCount}
                            isStudioCCFile={current.isStudioCCFile}
                            showVerifiedIcon={current.showVerifiedIcon}
                            width="100%"
                            height="360px"
                        />
                    ) : (
                        <Empty />
                    )}
                </div>
            </div>
        </div>
    );
}
